import * as Sentry from '@sentry/react';
import { Apollo } from 'src/gql/generated';

/**
 * Keeps the org playbook list in step with AI generation. A generated playbook
 * is created on the server by the chat agent, not by a mutation this page
 * runs, so no `update` callback adds it to the cached list the way the blank
 * and duplicate buttons do. The server announces the run org-wide the moment
 * it creates the (still empty) playbook, and again as each rule lands, so the
 * list listens to that and asks for itself again when a run names a playbook
 * it does not have.
 *
 * Rows already in the list need no refetch as rules arrive: the event carries
 * the playbook with its rule count, and Apollo normalizes that over the row.
 */
export function usePlaybooksListLiveUpdates({
  refetch,
}: {
  refetch: () => Promise<unknown>;
}): void {
  Apollo.useOnPlaybookGenerationJobUpdatedSubscription({
    ignoreResults: true,
    onData: ({ client, data }) => {
      const event = data.data?.playbookGenerationJobUpdated;
      if (!event) return;

      const listed = client.cache.readQuery<Apollo.GetPlaybooksQuery>({
        query: Apollo.GetPlaybooksDocument,
      });
      const listedPlaybookIds = listed?.playbooks.map(playbook => playbook.id) ?? null;
      if (getShouldRefetchPlaybooksList(event, listedPlaybookIds)) void refetch();
    },
    onError: error => {
      Sentry.captureException(error, { extra: { subscription: 'PlaybookGenerationJobUpdated' } });
    },
    shouldResubscribe: true,
  });
}

/**
 * Whether a generation event can have changed which playbooks the list holds.
 *
 * - A deleted job is a cancelled run, which takes an empty playbook with it.
 * - A failed run with nothing written also takes its playbook with it.
 * - A playbook the list has not seen was just created by a run starting.
 * - Anything else is progress on a listed playbook, which the event's own
 *   playbook fields keep current through the normalized cache.
 *
 * A list that is not cached yet counts as not having the playbook, so the
 * refetch is the safe answer there too.
 */
export function getShouldRefetchPlaybooksList(
  event: {
    generationJob: { failedAt: string | null; playbook: { id: string } | null } | null;
  },
  listedPlaybookIds: readonly string[] | null
): boolean {
  const { generationJob } = event;
  if (!generationJob) return true;
  if (generationJob.failedAt) return true;
  const playbookId = generationJob.playbook?.id;
  if (!playbookId) return false;
  return !listedPlaybookIds?.includes(playbookId);
}
